/**
 * TRUS-719 (Integrated Inbox, phase 4): "Maria is typing…" in member DMs.
 *
 *  - While a thread is open AND the page is visible, GET the typing list every
 *    3 s (the server excludes the caller and expires entries 6 s after the
 *    last POST).
 *  - While the composer has text and focus, POST at most once every 4 s,
 *    driven by keystrokes / focus (no keep-alive timer: someone who stops
 *    typing drops off after the server's 6 s expiry).
 *  - A 404 / 501 (from either call) hides the feature for the rest of the
 *    session, per surface (staff Inbox or member portal); other errors are
 *    ignored and the next poll tries again.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { TypingEntry } from '@/services/inboxPhase4Api';

export const TYPING_POLL_MS = 3_000;
export const TYPING_PING_MS = 4_000;

export type TypingSurface = 'staff' | 'member';

/** Surfaces whose backend answered 404 / 501 this session. */
const unavailable = new Set<TypingSurface>();

export function typingUnavailable(surface: TypingSurface): boolean {
  return unavailable.has(surface);
}

/** Tests only: forget the session's "unavailable" verdicts. */
export function resetTypingSession(): void {
  unavailable.clear();
}

interface TypingTransport {
  /** `null` = feature unavailable (404 / 501). */
  get: () => Promise<TypingEntry[] | null>;
  /** `false` = feature unavailable (404 / 501). */
  ping: () => Promise<boolean>;
}

interface UseTypingIndicatorArgs {
  surface: TypingSurface;
  /** Changes when the open thread changes (channel id / thread key); null = no thread. */
  threadKey: string | number | null;
  enabled: boolean;
  transport: TypingTransport;
}

function pageVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

export function useTypingIndicator({ surface, threadKey, enabled, transport }: UseTypingIndicatorArgs) {
  const [typing, setTyping] = useState<TypingEntry[]>([]);
  const [off, setOff] = useState(() => unavailable.has(surface));
  const transportRef = useRef(transport);
  transportRef.current = transport;
  const lastPing = useRef(0);

  const active = enabled && !off && threadKey != null;

  const disable = useCallback(() => {
    unavailable.add(surface);
    setOff(true);
    setTyping([]);
  }, [surface]);

  // Poll while the thread is open and the page is visible.
  useEffect(() => {
    setTyping([]);
    lastPing.current = 0;
    if (!active) return;
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const poll = async () => {
      if (!pageVisible()) return;
      try {
        const res = await transportRef.current.get();
        if (cancelled) return;
        if (res === null) {
          disable();
          return;
        }
        setTyping(res);
      } catch {
        /* transient: try again on the next tick */
      }
    };
    const start = () => {
      if (timer == null) timer = setInterval(poll, TYPING_POLL_MS);
    };
    const stop = () => {
      if (timer != null) clearInterval(timer);
      timer = null;
    };
    const onVisibility = () => {
      if (pageVisible()) {
        poll();
        start();
      } else {
        stop();
        setTyping([]);
      }
    };

    poll();
    start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [active, threadKey, disable]);

  /**
   * Call on every composer change / focus: POSTs (throttled to 4 s) when the
   * composer has text and focus.
   */
  const notifyComposer = useCallback(
    (text: string, focused: boolean) => {
      if (!active || !focused || !text.trim()) return;
      const now = Date.now();
      if (now - lastPing.current < TYPING_PING_MS) return;
      lastPing.current = now;
      transportRef.current
        .ping()
        .then((ok) => {
          if (!ok) disable();
        })
        .catch(() => {
          /* transient */
        });
    },
    [active, disable],
  );

  /** After a send the composer is empty: let the next keystroke ping at once. */
  const resetThrottle = useCallback(() => {
    lastPing.current = 0;
  }, []);

  return { typing: active ? typing : [], notifyComposer, resetThrottle, available: !off };
}
